<script setup lang="ts">

import { ChangelogCursorPagination } from '@widgets/changelog'
import { BadgeCustomerStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LoaderOverlay } from '@shared/ui/loaders'
import { LinkTextPrimary } from '@shared/ui/links'

import CustomerRowActions from './CustomerRowActions.vue'
import CustomerFullName from './CustomerFullName.vue'
import ModalDeleteCustomer from './modals/ModalDeleteCustomer.vue'
import { useTableCustomers } from '../../model/list/table-customers.hook'

defineOptions({
  name: 'TableCustomers',
})

const {
  customers,
  isLoading,
  hasCustomers,
  hasNext,
  hasPrev,
  perPage,
  count,
  canViewContacts,
  getStatus,
  getCreatedAt,
  getCustomerUrl,
  getKidUrl,
  goPrev,
  goNext,
  setChangePerPage,
} = useTableCustomers()
</script>

<template>
  <div :class="['table-customers', { 'table-customers--first-load': isLoading && !hasCustomers }]">
    <ChangelogCursorPagination
      :per-page="perPage"
      :count="count"
      :has-prev="hasPrev"
      :has-next="hasNext"
      :disabled="isLoading"
      :show-title="false"
      @change:per-page="setChangePerPage"
      @prev="goPrev"
      @next="goNext"
    />

    <AppTable
      :loading="isLoading"
      :paginated="false"
    >
      <template #head>
        <AppTr>
          <AppTh class="text-center table-customers__id-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              #
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Name
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Kids
            </AppText>
          </AppTh>
          <AppTh v-if="canViewContacts">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Email
            </AppText>
          </AppTh>
          <AppTh
            v-if="canViewContacts"
            class="table-customers__phone-cell"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Phone
            </AppText>
          </AppTh>
          <AppTh class="table-customers__status-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh class="table-customers__created-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Created
            </AppText>
          </AppTh>
          <AppTh class="table-customers__actions-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasCustomers"
        #body
      >
        <AppTr
          v-for="customer in customers"
          :key="customer.id"
        >
          <AppTd class="medium text-center">
            <LinkTextPrimary :href="getCustomerUrl(customer.id)">
              {{ customer.id }}
            </LinkTextPrimary>
          </AppTd>
          <AppTd class="medium">
            <CustomerFullName :customer="customer" />
          </AppTd>
          <AppTd class="medium">
            <div class="table-customers__kids">
              <LinkTextPrimary
                v-for="(kid, index) in customer.kids"
                :key="kid.id"
                :href="getKidUrl(kid.id)"
              >
                {{ kid.name }}{{ index < customer.kids.length - 1 ? ',' : '' }}
              </LinkTextPrimary>
            </div>
          </AppTd>
          <AppTd
            v-if="canViewContacts"
            class="medium"
          >
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ customer.email }}
            </AppText>
          </AppTd>
          <AppTd
            v-if="canViewContacts"
            class="medium table-customers__nowrap"
          >
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ customer.phone }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <BadgeCustomerStatus :status="getStatus(customer)" />
          </AppTd>
          <AppTd class="medium table-customers__nowrap">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getCreatedAt(customer) }}
            </AppText>
          </AppTd>
          <AppTd class="zero-padding table-customers__actions-cell">
            <CustomerRowActions :customer="customer" />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>

    <ChangelogCursorPagination
      :per-page="perPage"
      :count="count"
      :has-prev="hasPrev"
      :has-next="hasNext"
      :disabled="isLoading"
      :show-title="false"
      @change:per-page="setChangePerPage"
      @prev="goPrev"
      @next="goNext"
    />

    <LoaderOverlay v-if="isLoading" />

    <ModalDeleteCustomer />
  </div>
</template>

<style scoped lang="scss" src="./TableCustomers.scss" />
